import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { FormAdapter, FormAlert, FormContainerOptions, FormControlType, FormControlProperties, FormValues } from '@omegagrid/form';
import rp from "@reporting/rp-app-host";
import { User, AccountingGroup } from "@reporting/rp-types/core";
import { PASSWORD_POLICIES, PasswordPolicy } from "@reporting/rp-types/accounting";
import { CallerContext } from "../types";

export class PasswordFormAdapter extends FormAdapter {

	private pwdPolicy: PasswordPolicy;

	constructor(
		private group: AccountingGroup,
		private user: User,
		private caller: CallerContext = 'user',
		private options?: FormContainerOptions
	) {
		super();
		this.pwdPolicy = PASSWORD_POLICIES[group?.password_complexity || 1];
	}

	getColumns() {
		return [
			{width: 180},
			{width: null},
		];
	}

	async getControls() {
		let r = 0;
		const rows: FormControlProperties[] = [];
		if (this.caller === 'user') {
			rows.push(
				{row: ++r, col: 0, type: FormControlType.Label, value: msg('Original password'), for: '_origPassword'},
				{row: r, col: 1, type: FormControlType.PasswordInput, key: '_origPassword'},
			);
		}
		rows.push(
			{row: ++r, col: 0, type: FormControlType.Label, value: msg('Password'), for: 'password'},
			{row: r, col: 1, type: FormControlType.PasswordInput, key: 'password', pattern: this.pwdPolicy.pattern, placeholder: msg(this.pwdPolicy.text), options: {
				showToggle: true
			}},
			{row: ++r, col: 0, type: FormControlType.Label, value: msg('Confirm Password'), for: '_confirmPassword'},
			{row: r, col: 1, type: FormControlType.PasswordInput, key: '_confirmPassword', pattern: this.pwdPolicy.pattern, options: {
				showToggle: true
			}}
		);
		return rows;
	}

	getOptions(): FormContainerOptions {
		return this.options || {
			caption: msg('Password change'),
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: [{ key: 'save', text: msg('Change password'), color: ColorTypes.Accent }],
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		if (!formValues['password'] || !formValues['_confirmPassword'] || (this.caller === 'user' && !formValues['_origPassword'])) {
			rp.host.bucket.showAlert({content: msg('Please fill in all fields.'), type: 'warning', title: msg('User settings')});
			return false;
		}
		if (this.caller === 'user') {
			const pwdVerify = await rp.ms('auth').post('verify', {
				login: this.user.login_name,
				password: formValues['_origPassword']
			});
			if (!pwdVerify) {
				rp.host.bucket.showAlert({content: msg('Invalid original password.'), type: 'warning', title: msg('User settings')});
				return false;
			}
		}
		if (formValues['password'] !== formValues['_confirmPassword']) {
			rp.host.bucket.showAlert({content: msg('Passwords do not match.'), type: 'warning', title: msg('User settings')});
			return false;
		}
		this.user.password = formValues['password'] as string;
		this.user.idz = undefined;
		this.user = await rp.ms('users').post<User>('users', Object.assign({ g: this.group?.id_group || null }, this.user));
		rp.host.bucket.showAlert({content: msg('Password changed successfully.'), type: 'success', title: msg('User settings')});
		return true;
	}
};
